import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-executive-1.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryExec from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryLounge from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1400&imagetools";
import gallerySuv from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/chauffeur-service-atlanta";
const PAGE_TITLE = "Chauffeur Service Atlanta | Private & Airport Chauffeurs 24/7";
const PAGE_DESC =
  "Professional chauffeurs in Atlanta for airport pickups, executives and family visits. Book by phone or text, track your chauffeur live in our app. Rated 5.0 on Google.";

const faqs = [
  {
    q: "Can I book an airport pickup for my parents or a family member?",
    a: "Yes, it's one of our most common requests. Book by phone or text and we send the chauffeur's name, phone number and vehicle to you, to them, or to both. Your family doesn't need an app or a smartphone. Add meet-and-greet (a small additional fee) and the chauffeur meets them inside baggage claim with a name card and carries their bags.",
  },
  {
    q: "How will I find my chauffeur at Hartsfield-Jackson?",
    a: "Your chauffeur waits in the cell lot until you confirm you're at baggage claim, then texts you a numbered door and pulls up there. International arrivals are picked up at the International Terminal curb after customs. If you'd rather not look for anyone, choose meet-and-greet and your chauffeur waits inside near the carousel with a name card.",
  },
  {
    q: "What if my flight is delayed or lands early?",
    a: "We track your flight on every airport transfer, so your chauffeur's timing moves with it. There's no need to call us from the plane.",
  },
  {
    q: "Can I book the same chauffeur every time?",
    a: "Many of our regular clients do. Tell us which chauffeur you liked and we'll request them for your next trip whenever their schedule allows. For multi-day visits we assign one chauffeur for the whole stay.",
  },
  {
    q: "How much does a chauffeur service cost in Atlanta?",
    a: "Atlanta chauffeur rates depend on vehicle, hours, and route. Mercedes-Benz Sprinter Limousine service starts from $250/hr, and custom quotes are given upon request. Airport transfers are quoted as flat rates.",
  },
  {
    q: "What does a professional chauffeur do?",
    a: "A chauffeur is a professionally trained, licensed, insured driver assigned to you and your vehicle for the duration of your booking. Expect a suit, door service, luggage handling, route knowledge and complete discretion. Mine have been with my family for years.",
  },
  {
    q: "Do you offer hourly and as-directed chauffeur service?",
    a: "Yes. Most of our corporate and event clients book by the hour or on as-directed schedules, meetings across town, dinner-to-event runs, multi-stop wedding days, and full-day executive itineraries with the same chauffeur and vehicle. Hourly service has a 4-hour minimum.",
  },
  {
    q: "Can I book a chauffeur for a wedding or special event?",
    a: "Absolutely. Weddings, galas, anniversaries, proposals, and bachelor/bachelorette events are a core part of our business. We coordinate timing with your planner and provide a chauffeur dressed to match the formality of the event.",
  },
  {
    q: "How far in advance should I book a chauffeur in Atlanta?",
    a: "For executive transfers and airport pickups, 24–48 hours is ideal. For weddings, holidays (Masters week, Super Bowl, Final Four), and peak event nights, book 2–4 weeks ahead, fleet sells out.",
  },
  {
    q: "Do chauffeurs cover the entire metro Atlanta area?",
    a: "Yes, Buckhead, Midtown, Downtown, Sandy Springs, Alpharetta, Dunwoody, Vinings, Smyrna, Marietta, Decatur, Brookhaven, Roswell, and every Atlanta-area airport (ATL, PDK, FTY, RYY, LZU).",
  },
  {
    q: "What does an as-directed (hourly) chauffeur day actually look like?",
    a: "Your chauffeur and vehicle stay with you for the booked block. A typical executive day: 7:30am hotel pickup in Buckhead, three meetings between Midtown and Perimeter, a lunch stop, then a 5:15pm drop at PDK. Between stops the chauffeur waits on-site with the vehicle staged, you never re-book, re-hail, or wait.",
  },
  {
    q: "How early does the chauffeur arrive?",
    a: "Standard is 15 minutes before your scheduled time, staged and ready. For airport departures and wedding days we stage 20–30 minutes early. You'll get a text with the chauffeur's name, phone number, and vehicle description before every pickup.",
  },
  {
    q: "Can one chauffeur cover a multi-day itinerary?",
    a: "Yes, and it's what most repeat clients ask for. We assign a single chauffeur to a multi-day program so they learn your preferences, cabin temperature, route habits, coffee stop, whether you want conversation or silence. Long days over 10 hours are split with a second chauffeur for hours-of-service compliance.",
  },
  {
    q: "Do you coordinate directly with my assistant or planner?",
    a: "Constantly. Roughly half our bookings come from executive assistants, hotel concierges, wedding planners, and flight departments. We work off your itinerary document, confirm each leg in writing, and send day-of updates to whoever you designate rather than interrupting the principal.",
  },
  {
    q: "What happens if a meeting runs long or plans change mid-day?",
    a: "Nothing dramatic, call or text your chauffeur directly. As-directed bookings flex in place; we only bill the additional time in 30-minute increments. If a change requires a different vehicle or an added leg, our team confirms availability before anything is charged.",
  },
  {
    q: "Are your chauffeurs background-checked and insured?",
    a: "Every chauffeur is background-checked, drug-tested, DOT-compliant, and covered under our commercial liability policy. Certificates of insurance are available on request for corporate and venue vendor requirements.",
  },
];

export const Route = createFileRoute("/chauffeur-service-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "chauffeur service Atlanta, Atlanta chauffeur, private chauffeur Atlanta, luxury chauffeur Atlanta, executive car service Atlanta, hourly chauffeur Atlanta, black car chauffeur Atlanta",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Private Chauffeur Service",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Private Chauffeur Service",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Buckhead" },
              { type: "City", name: "Midtown Atlanta" },
              { type: "City", name: "Sandy Springs" },
              { type: "City", name: "Alpharetta" },
              { type: "Airport", name: "Hartsfield-Jackson Atlanta International Airport (ATL)" },
              { type: "Airport", name: "DeKalb-Peachtree Airport (PDK)" },
            ],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(faqs)),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Atlanta • Private Chauffeur Service"
      h1="Private Chauffeur Service in Atlanta"
      intro="Your day already has enough moving parts. My chauffeurs handle airport pickups, executive travel and family visits across metro Atlanta, 24/7, in Suburban SUVs and Mercedes-Benz Sprinter Limousines."
      heroImage={heroImage}
      heroAlt="Professional chauffeur opening door of luxury Mercedes-Benz Sprinter Limousine in Atlanta"
      midImage={midImage}
      midAlt="Interior of Mercedes-Benz Sprinter Limousine, leather seating and ambient lighting for Atlanta chauffeur service"
      sectionOneTitle="A Chauffeur You Can Feel Comfortable With"
      sectionOneBody={[
        "My chauffeurs are an extension of my family: full-time professionals who are fingerprinted and background-checked. We are licensed with the Georgia Department of Public Safety and carry commercial insurance.",
        "From PDK pickups to Buckhead weddings and full-day meetings through Midtown and Sandy Springs, we plan around your itinerary. Choose a Suburban for a smaller party or a Sprinter for your group.",
        "We look after executives, private flyers, wedding parties, touring artists, film productions and families. Text me what your day looks like.",
      ]}
      bulletsTitle="Why Atlanta Books Our Chauffeurs"
      bulletsIntro="Careful driving, discretion and a plan built around you."
      bullets={[
        "Professionally trained, full-time chauffeurs",
        "Suburban SUVs for up to 6 or 7; Mercedes-Benz Sprinter Limousines for groups",
        "Hourly, as-directed, and point-to-point service",
        "Live flight tracking for every airport transfer",
        "24/7 support with a real person on the line",
        "Georgia DPS licensing and commercial insurance",
        "Discretion and confidentiality with every booking",
      ]}
      coverageTitle="Chauffeur Coverage Across Metro Atlanta"
      coverageIntro="Door-to-door chauffeured transportation throughout the city, suburbs, and every Atlanta-area airport."
      coverage={[
        "Buckhead",
        "Midtown Atlanta",
        "Downtown Atlanta",
        "Sandy Springs",
        "Alpharetta",
        "Dunwoody",
        "Vinings & Smyrna",
        "Marietta",
        "Brookhaven",
        "Roswell",
        "Decatur",
        "ATL, PDK, FTY, RYY & LZU",
      ]}
      routeTable={{
        title: "Common Chauffeured Routes Across Metro Atlanta",
        intro:
          "Point-to-point drive times we run every week. Add 15–30 minutes during the weekday 6:30–9:30am and 3:30–7:00pm peaks.",
        rows: [
          { route: "Buckhead → Downtown", distance: "~8 mi", time: "15–30 min", notes: "GA-400 to the Connector; Peachtree Rd is the reliable backup." },
          { route: "Buckhead → PDK Airport", distance: "~11 mi", time: "20–30 min", notes: "I-85 N to Clairmont; fastest private-aviation run in the city." },
          { route: "Midtown → Hartsfield-Jackson", distance: "~13 mi", time: "25–40 min", notes: "Connector southbound; departures leave 2h45m before wheels-up." },
          { route: "Alpharetta → Downtown", distance: "~26 mi", time: "40–70 min", notes: "GA-400 S; the single most traffic-sensitive route we run." },
          { route: "Sandy Springs → The Battery / Truist Park", distance: "~13 mi", time: "25–45 min", notes: "I-285 W; game-day arrival 90 minutes before first pitch." },
          { route: "Downtown → Marietta Square", distance: "~20 mi", time: "30–50 min", notes: "I-75 N; wedding and rehearsal-dinner staple." },
          { route: "Atlanta → Chateau Elan (Braselton)", distance: "~45 mi", time: "50–75 min", notes: "I-85 N; frequent wedding and golf weekend transfer." },
          { route: "Atlanta → Serenbe", distance: "~32 mi", time: "45–60 min", notes: "I-85 S to Chattahoochee Hills; rural final miles." },
        ],
        footnote:
          "Drive times come from our own trip history on these routes, not best-case mapping estimates.",
      }}
      detailSections={[
        {
          title: "Airport Pickups Without the Worry",
          body: [
            "Most airport calls start with the same questions: Will someone actually be there? How do I find them? What if the flight is late? Here's how each one is handled before you land.",
          ],
          list: [
            "Someone is there. Your chauffeur's name, phone number and vehicle are sent before pickup, and you can track your chauffeur live in our app.",
            "You'll find them easily. At ATL your chauffeur texts you a numbered baggage-claim door, or meets you inside with a name card if you choose meet-and-greet.",
            "Late flights are handled. We track your flight on every airport transfer, so timing adjusts without a phone call.",
            "Booking for someone else is simple. Book for parents or guests by phone or text; they don't need an app, and updates can come to you.",
            "A real person answers. Call or text (678) 431-7222 any time, day or night.",
            "Licensed and insured. Every chauffeur is background-checked and covered under our commercial liability policy.",
          ],
        },
        {
          title: "How Our Chauffeur Service Is Booked",
          body: [
            "Three structures cover nearly every request. Pick the one that fits your day and we build the rest around it.",
          ],
          list: [
            "Point-to-point, a single transfer with a flat, confirmed rate. Airport runs, dinner reservations, venue drops.",
            "Hourly / as-directed, the chauffeur and vehicle stay with you for a booked block, with a custom quote given upon request. Executive days, wedding days, shopping and tour days.",
            "Multi-day programs, the same chauffeur across a full visit, with a written itinerary and one point of contact.",
            "Roadshows & corporate travel, multi-vehicle coordination with staged departures and a single point of contact for your EA.",
          ],
        },
        {
          title: "Wedding Day Chauffeur Timeline",
          body: [
            "Wedding transportation fails on timing, not vehicles. Here's the structure we run for Atlanta weddings, adjusted to your planner's schedule.",
          ],
          list: [
            "Vehicle staged at the getting-ready location 30 minutes before the first movement.",
            "Bridal party transfer to the ceremony venue, arriving 45 minutes before the ceremony start.",
            "Couple's private transfer between ceremony and reception, the only quiet 20 minutes of your day.",
            "Guest transfer loops between hotel block and reception, typically 3–4 runs across the evening.",
            "Late-night departure and hotel returns after last dance, including a designated final sweep.",
          ],
        },
        {
          title: "What's Standard in Every Vehicle",
          list: [
            "Chilled bottled water and phone charging for every seat.",
            "Climate zones the passengers control, not the driver.",
            "Vehicle detailed before every trip, interior photographed at staging.",
            "Wi-Fi on Sprinter bookings for working transfers.",
            "Chauffeur in suit and tie unless you request business casual for a relaxed itinerary.",
          ],
        },
      ]}
      gallery={{
        title: "The Vehicles Your Chauffeur Arrives In",
        intro: "Mercedes-Benz Sprinters and Suburban SUVs, detailed before every trip.",
        images: [
          { src: galleryExec, alt: "Executive Mercedes-Benz Sprinter used for Atlanta chauffeur service", caption: "Executive Sprinter, corporate days and team travel" },
          { src: galleryLounge, alt: "Sprinter Limousine lounge interior for Atlanta private chauffeur bookings", caption: "Sprinter Limousine, lounge cabin for groups up to 14" },
          { src: gallerySuv, alt: "Suburban SUV interior for Atlanta executive chauffeur transfers", caption: "Suburban SUV, solo executives and couples" },
        ],
      }}
      faqTitle="Atlanta Chauffeur Service FAQs"
      relatedTitle="Related Atlanta Chauffeur Services"
      faqs={faqs}
      related={[
        {
          to: "/corporate-transportation-atlanta",
          title: "Corporate Transportation Atlanta",
          desc: "Dedicated corporate accounts for executive teams and their assistants.",
        },
        {
          to: "/private-jet-car-service-atlanta",
          title: "Private Jet Car Service Atlanta",
          desc: "FBO-side chauffeur service for private aviation clients.",
        },
        {
          to: "/chauffeur-service-buckhead-from-airport",
          title: "Buckhead Airport Chauffeur",
          desc: "Direct chauffeured transfers into Buckhead from every Atlanta airport.",
        },
        {
          to: "/atlanta-airport-car-service",
          title: "Atlanta Airport Car Service",
          desc: "Hartsfield-Jackson pickups with flight tracking and meet-and-greet.",
        },
      ]}
      ctaTitle="Text Me Your Itinerary"
      ctaBody="Tell me your date, stops and passenger count. I'll check availability personally and send your quote in writing."
    />
  );
}
